import { setIn } from 'final-form';

import { matchPath } from 'react-router';

import { call, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { v4 as uuidv4 } from 'uuid';

import { restClient } from 'config/store/configureStore';
import * as legacyTypes from 'constants/ActionTypes';
import { TECHNICIAN } from 'constants/ModeConstants';
import { PATH } from 'constants/RoutesConstants';
import { SELF_SERVICE_TYPE } from 'constants/SelfServiceConstants';

import { generateQRCode } from 'modules/app/actions';
import * as interventionCoreActions from 'modules/interventions/actions/core';
import * as interventionDetailsActions from 'modules/interventions/actions/details';
import * as interventionTypes from 'modules/interventions/actionTypes';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import * as actions from 'modules/selfServices/actions';
import { modelFormatter } from 'modules/selfServices/actions.utils';
import * as actionTypes from 'modules/selfServices/actionTypes';
import { fetchServiceHeader } from 'modules/serviceHeaders/actions';
import { FETCH_SERVICE_HEADER_FAILURE, FETCH_SERVICE_HEADER_SUCCESS } from 'modules/serviceHeaders/actionTypes';
import { createNotification } from 'modules/toast-notifications/actions';
import { getUserGroups } from 'modules/userGroups/actions';

import { getSelectedDealerId, checkSelfServiceKioskByDealerId } from 'selectors/dealerSelectors';
import { getCurrentLocation } from 'selectors/router';
import { downloadFile } from 'services/Utils';
import { camelCase } from 'utils/global';

import extrasDashboard from './extras.dashboard';
import questionsDashboard from './questions.dashboard';
import vehiclesCheckDashboard from './vehiclesCheck.dashboard';

function* startSelfServiceQuestionDetailsApplication() {
  const location = yield select(getCurrentLocation);
  const match =
    matchPath(PATH.QUESTION_MODEL_DETAILED.path, location) ??
    matchPath(PATH.GROUP_QUESTION_MODEL_DETAILED.path, location);
  const dealerId = yield select(getSelectedDealerId);

  if (match && dealerId) {
    yield put(getUserGroups(dealerId));
  }
}

const DMS_PROPERTY_TO_SELF_SERVICE = {
  mobilityInfo: 'mobility.vehicle',
};

function* addDMSInformation(model, dealerId, search) {
  if (search) {
    yield put(fetchServiceHeader(dealerId, search, { autoSelect: false, hideLoader: true }));
    const { payload: result, error } = yield take([FETCH_SERVICE_HEADER_SUCCESS, FETCH_SERVICE_HEADER_FAILURE]);

    if (!error) {
      const [dmsInfo = {}] = result;

      return Object.entries(DMS_PROPERTY_TO_SELF_SERVICE).reduce(
        (acc, [dmsProps, selfServiceProps]) => setIn(acc, selfServiceProps, dmsInfo[dmsProps]),
        model
      );
    }
  }
  return model;
}

/** @param {import('../types').GetModelAction} action */
function* fetchModel({ payload, meta = {} }) {
  const { request, dms: dmsSearch } = payload;
  try {
    const { data: model } = yield call(restClient.request, request);
    const id = yield call(uuidv4);
    const additionalFields = model.type === SELF_SERVICE_TYPE.CHECK_OUT ? { displayParking: true } : {};

    // If defined - The model is completed with the dms info
    const modelWithDMS = yield call(addDMSInformation, model, payload.request.params.dealerId, dmsSearch);

    yield put(actions.fetchModelSuccess({ ...modelWithDMS, id, ...additionalFields }, meta));
  } catch (error) {
    yield put(actions.fetchModelFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.form.selfService.enableFailed',
          defaultMessage: "Can't enable self service",
        },
      })
    );
  }
}

/** @param {import('../types').GetSummaryPdfAction} action */
function* getSummaryPdf({ payload }) {
  const { request, fileName } = payload;

  try {
    const { data: file } = yield call(restClient.request, request);
    downloadFile(file, { fileName, type: 'application/pdf' });
    yield put(actions.getSummaryPdfSuccess());
  } catch (error) {
    yield put(actions.getSummaryPdfFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage: 'Oops! Something went wrong.',
        },
      })
    );
  }
}

/** @param {import('../types').SaveInterventionAndSendSelfServiceAction} action */
function* saveAndSendWorker({ payload: { intervention, channels, type, ignoreRights }, meta }) {
  // In first, save the intervention
  yield put(
    interventionCoreActions.updateInterventionRequest(intervention, { disableAutoSendEvent: true }, { silent: true })
  );
  const action = yield take([interventionTypes.UPDATE_INTERVENTION_FAILURE, interventionTypes.UPDATE_INTERVENTION_SUCCESS]);
  if (action.type === interventionTypes.UPDATE_INTERVENTION_SUCCESS) {
    // Second step, send the selfService
    const selfServiceId = action.payload.selfServices?.find(selfService => selfService.type === type)?.id;
    yield put(actions.sendSelfService(selfServiceId, channels, type, ignoreRights, meta));
  }
}

/** @param {import('../types').SendSelfServiceRequestAction} */
function* sendSelfServiceWorker({ payload: { request, type }, meta }) {
  const translationKey = camelCase(`send_Home_${type}`);

  try {
    const { data: selfService } = yield call(restClient.request, request);
    yield put(actions.sendSelfServiceSuccess(selfService, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: `page.fiche.confirmation.${translationKey}.success`,
          defaultMessage: `${type} sent`,
        },
      })
    );
  } catch (error) {
    yield put(actions.sendSelfServiceFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: `page.fiche.confirmation.${translationKey}.failure`,
          defaultMessage: `Can't send the ${type}`,
        },
      })
    );
  }
}

/** @param {import('../types').OpenSendWizardAction} action */
function* onOpenSendSelfServiceWizardWorker({ payload: { interventionId, type } }) {
  try {
    // In first - Fetch the latest intervention version
    yield put(interventionCoreActions.fetchIntervention(interventionId, TECHNICIAN, { silent: true }));
    const fetchResponse = yield take([interventionTypes.FETCH_INTERVENTION_SUCCESS, interventionTypes.FETCH_INTERVENTION_FAILURE]);

    if (fetchResponse.type === interventionTypes.FETCH_INTERVENTION_FAILURE) {
      throw fetchResponse.payload;
    }

    const { payload: intervention } = fetchResponse;
    let defaultSelfService;
    const hasSelfService = intervention.selfServices?.some(({ type: selfServiceType }) => selfServiceType === type);

    // If self service does not exist, load the default model
    if (!hasSelfService) {
      const { module, brandId } = intervention;
      const dealerId = intervention.dealerId ?? intervention.dealer?.id;
      yield put(actions.fetchModel({ module, brandId, dealerId, type, interventionId }));
      const modelResponse = yield take([actionTypes.FETCH_MODEL_SUCCESS, actionTypes.FETCH_MODEL_FAILURE]);

      if (modelResponse.type === actionTypes.FETCH_MODEL_FAILURE) {
        throw modelResponse.payload;
      }

      // Check the configuration and force to set keyDepositEnabled by default
      if (type === SELF_SERVICE_TYPE.CHECK_OUT) {
        modelResponse.payload.keyDepositEnabled = yield select(state =>
          checkSelfServiceKioskByDealerId(state, dealerId));
      }

      defaultSelfService = modelFormatter(modelResponse.payload);
    }

    yield put(actions.isSendWizardReady(defaultSelfService));
  } catch {
    const notification = {
      type: 'error',
      message: {
        id: 'http.error.message',
        defaultMessage: 'Oops! Something went wrong.',
      },
    };
    yield put(createNotification(notification));
  }
}

/** @param {import('../types').GetOnSiteLinkAction} action */
function* getOnSiteLink({ payload, meta }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.getOnSiteLinkSuccess(data, meta));
    if (meta.download) {
      yield put(
        generateQRCode({
          url: data.url,
          filename: 'self-service-qr-code.png',
        })
      );
    }
  } catch (error) {
    yield put(actions.getOnSiteLinkFailure(error, meta));
    const notification = {
      type: 'error',
      message: {
        id: 'http.error.message',
        defaultMessage: 'Oops! Something went wrong.',
      },
    };
    yield put(createNotification(notification));
  }
}

/** @param {import('../types').DeleteMobilityAction} action */
function* deleteMobilityWorker({ payload: { request, mobilityId }, meta }) {
  try {
    yield call(restClient.request, request);
    yield put(actions.deleteMobilitySuccess(mobilityId, meta));
  } catch (error) {
    yield put(actions.deleteMobilityFailure(error, meta));
  }
}

function* afterSaveWorker({ meta }) {
  if (meta?.action === 'resend') {
    yield put(interventionDetailsActions.openResendModal(meta));
  }
}

export default function* selfServicesSagas() {
  yield fork(extrasDashboard);
  yield fork(questionsDashboard);
  yield fork(vehiclesCheckDashboard);

  yield takeEvery(actionTypes.FETCH_MODEL, fetchModel);
  yield takeLatest(actionTypes.FETCH_SUMMARY_PDF, getSummaryPdf);

  yield takeLatest(actionTypes.OPEN_SEND_WIZARD, onOpenSendSelfServiceWizardWorker);
  yield takeLatest(actionTypes.SAVE_INTERVENTION_AND_SEND_SELF_SERVICE, saveAndSendWorker);
  yield takeLatest(actionTypes.SEND_SELF_SERVICE, sendSelfServiceWorker);
  yield takeLatest(actionTypes.GET_ON_SITE_LINK, getOnSiteLink);
  yield takeLatest([LOCATION_CHANGE, legacyTypes.SELECT_DEALER], startSelfServiceQuestionDetailsApplication);
  yield takeLatest(actionTypes.DELETE_MOBILITY, deleteMobilityWorker);
  yield takeLatest(interventionTypes.UPDATE_INTERVENTION_SUCCESS, afterSaveWorker);
}
